import { ArrowLeft, ArrowRight } from 'lucide-react'

interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizes?: number[]
}

function pageNumbers(current: number, last: number): (number | '…')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)
  const out: (number | '…')[] = [1]
  const from = Math.max(2, current - 1)
  const to = Math.min(last - 1, current + 1)
  if (from > 2) out.push('…')
  for (let i = from; i <= to; i++) out.push(i)
  if (to < last - 1) out.push('…')
  out.push(last)
  return out
}

/** 44px footer, Plus Jakarta 12px, "1–N of N items" (design system, Pagination). */
export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, pageSizes = [25, 50, 100] }: PaginationProps) {
  const last = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div
      className="flex items-center justify-between bg-background-main flex-shrink-0"
      style={{ height: 44, padding: '0 20px', borderTop: '0.5px solid #4B5563' }}
    >
      <div className="flex items-center font-jakarta" style={{ gap: 10, fontSize: 12 }}>
        {onPageSizeChange && (
          <>
            <span className="text-text-support">Per page</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-background-body text-white font-jakarta outline-none"
              style={{ border: '0.5px solid #4B5563', fontSize: 12, padding: '2px 6px', height: 24 }}
            >
              {pageSizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
        <span className="text-text-support">{from}–{to} of {total} items</span>
      </div>

      <div className="flex items-stretch" style={{ border: '0.5px solid #4B5563', height: 24 }}>
        <button
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={`font-jakarta flex items-center ${page <= 1 ? 'text-text-support cursor-not-allowed' : 'text-white cursor-pointer'}`}
          style={{ fontSize: 12, padding: '0 8px', gap: 4, borderRight: '0.5px solid #4B5563', background: 'none', border: 'none' }}
        >
          <ArrowLeft size={12} /> Previous
        </button>
        {pageNumbers(page, last).map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="font-jakarta text-text-support flex items-center justify-center"
              style={{ width: 24, fontSize: 12, borderLeft: '0.5px solid #4B5563' }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`font-jakarta flex items-center justify-center ${p === page ? 'bg-background-body text-white' : 'text-text-support'}`}
              style={{ width: 24, fontSize: 12, borderLeft: '0.5px solid #4B5563', background: p === page ? '#1A1D21' : 'none', border: 'none', cursor: 'pointer' }}
            >
              {p}
            </button>
          ),
        )}
        <button
          disabled={page >= last}
          onClick={() => onPageChange(page + 1)}
          className={`font-jakarta flex items-center ${page >= last ? 'text-text-support cursor-not-allowed' : 'text-white cursor-pointer'}`}
          style={{ fontSize: 12, padding: '0 8px', gap: 4, borderLeft: '0.5px solid #4B5563', background: 'none', border: 'none' }}
        >
          Next <ArrowRight size={12} />
        </button>
      </div>
    </div>
  )
}
